import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  useTheme,
} from "@mui/material";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { FilterValue } from "../../../../../models/Filters/FilterValue";

export interface PodDeleteSettlementLine {
  stock_issue_request_id: number;
  sku: string;
  product_name: string | null;
  debited_qty: number;
}

export interface PodDeleteClientInventoryOption {
  id: number;
  label: string;
  is_master: boolean;
}

interface PodDeleteDraftLine extends PodDeleteSettlementLine {
  to_swageazy: number;
  to_client: number;
}

interface PodDeleteSettlementDialogProps {
  open: boolean;
  lines: PodDeleteSettlementLine[];
  clientInventories?: PodDeleteClientInventoryOption[];
  onClose: () => void;
  onConfirm: (payload: {
    lines: {
      stock_issue_request_id: number;
      to_swageazy: number;
      to_client: number;
    }[];
    client_inventory_id?: number;
  }) => void;
}

const parseQty = (raw: string) => Math.max(0, Math.trunc(Number(raw) || 0));

const PodDeleteSettlementDialog: React.FC<PodDeleteSettlementDialogProps> = ({
  open,
  lines,
  clientInventories = [],
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const [drafts, setDrafts] = useState<PodDeleteDraftLine[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [clientInventoryId, setClientInventoryId] = useState<number | null>(
    null,
  );

  const needsInventoryPicker = clientInventories.length > 1;
  const inventoryOptions: FilterValue[] = useMemo(
    () =>
      clientInventories.map((inventory) => {
        const label = inventory.is_master
          ? `${inventory.label} (Master)`
          : inventory.label;
        return {
          id: String(inventory.id),
          value: `${inventory.id} -${label}`,
        };
      }),
    [clientInventories],
  );

  useEffect(() => {
    if (!open) return;
    setDrafts(
      lines.map((line) => ({
        ...line,
        to_swageazy: line.debited_qty,
        to_client: 0,
      })),
    );
    setClientInventoryId(
      clientInventories.find((inventory) => inventory.is_master)?.id ??
        clientInventories[0]?.id ??
        null,
    );
    setSubmitError("");
  }, [open, lines, clientInventories]);

  const updateQty = (
    index: number,
    field: "to_swageazy" | "to_client",
    raw: string,
  ) => {
    const next = parseQty(raw);
    setSubmitError("");
    setDrafts((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: next } : row,
      ),
    );
  };

  const hasMismatch = drafts.some(
    (line) => line.to_swageazy + line.to_client !== line.debited_qty,
  );
  const hasClientQty = drafts.some((line) => line.to_client > 0);

  const handleConfirm = () => {
    const mismatch = drafts.find(
      (line) => line.to_swageazy + line.to_client !== line.debited_qty,
    );
    if (mismatch) {
      const assigned = mismatch.to_swageazy + mismatch.to_client;
      setSubmitError(
        `${mismatch.sku} was taken as ${mismatch.debited_qty}. You entered ${mismatch.to_swageazy} for Swageazy and ${mismatch.to_client} for the client (${assigned}). Those two numbers must add up to ${mismatch.debited_qty}.`,
      );
      return;
    }
    if (needsInventoryPicker && hasClientQty && clientInventoryId == null) {
      setSubmitError(
        "This customer has more than one inventory. Choose which inventory should receive the client quantity.",
      );
      return;
    }
    onConfirm({
      lines: drafts.map((line) => ({
        stock_issue_request_id: line.stock_issue_request_id,
        to_swageazy: line.to_swageazy,
        to_client: line.to_client,
      })),
      ...(needsInventoryPicker && hasClientQty && clientInventoryId != null
        ? { client_inventory_id: clientInventoryId }
        : {}),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <CustomTypography variant="heading4" fontWeight={700}>
          Delete POD order
        </CustomTypography>
      </DialogTitle>
      <DialogContent>
        <CustomTypography
          variant="body2"
          color={colors.grayText}
          sx={{ mb: 2 }}
        >
          Each item shows how many pieces were taken from Swageazy. Split that
          number between the two inventories. The two quantities must add up to
          the taken quantity.
        </CustomTypography>
        {needsInventoryPicker && (
          <Box sx={{ mb: 2 }}>
            <AppDropDown
              title="Client inventory"
              isMultiSelect={false}
              list={inventoryOptions}
              value={
                clientInventoryId != null ? String(clientInventoryId) : null
              }
              onSingleSelectionChange={(id) => {
                setSubmitError("");
                setClientInventoryId(Number(id));
              }}
              width="100%"
              menuZIndex={1500}
              required={hasClientQty}
              placeholderText="Select inventory"
            />
            <CustomTypography
              variant="body3"
              color={colors.grayText}
              sx={{ mt: 0.5 }}
            >
              Required when any quantity moves to the client. This customer has
              more than one inventory.
            </CustomTypography>
          </Box>
        )}
        {drafts.map((line, index) => {
          const assigned = line.to_swageazy + line.to_client;
          const over = assigned > line.debited_qty;
          const short = assigned < line.debited_qty;
          const message = over
            ? `${line.to_swageazy} + ${line.to_client} = ${assigned}. Only ${line.debited_qty} was taken, so ${assigned} cannot be moved.`
            : short
              ? `${line.to_swageazy} + ${line.to_client} = ${assigned}. ${line.debited_qty - assigned} of ${line.debited_qty} still needs an inventory.`
              : `${line.to_swageazy} back to Swageazy and ${line.to_client} to the client. That uses all ${line.debited_qty}.`;

          return (
            <Box
              key={line.stock_issue_request_id}
              sx={{
                border: "1px solid",
                borderColor: over
                  ? theme.palette.blushPink.main
                  : colors.lightGray,
                backgroundColor: over
                  ? theme.palette.blushPink.main
                  : colors.veryLightGray,
                borderRadius: 1.5,
                p: 1.5,
                mb: 1.5,
              }}
            >
              <CustomTypography variant="body1" fontWeight={700}>
                {line.product_name || "Product"}
              </CustomTypography>
              <Box sx={{ mb: 1 }}>
                <SkuCopyRow sku={line.sku} color={colors.grayText} />
              </Box>
              <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 1.25 }}>
                Taken from Swageazy: {line.debited_qty}
              </CustomTypography>
              <Grid2 container spacing={1.5}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomTypography variant="heading8" sx={{ mb: 0.5 }}>
                    Move back to Swageazy
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    type="number"
                    placeholder="0"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                    value={line.to_swageazy}
                    onChange={(event) =>
                      updateQty(index, "to_swageazy", event.target.value)
                    }
                  />
                  <CustomTypography variant="body3" color={colors.grayText}>
                    Pieces returned to Swageazy boxes
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomTypography variant="heading8" sx={{ mb: 0.5 }}>
                    Move to client inventory
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    type="number"
                    placeholder="0"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                    value={line.to_client}
                    onChange={(event) =>
                      updateQty(index, "to_client", event.target.value)
                    }
                  />
                  <CustomTypography variant="body3" color={colors.grayText}>
                    Pieces added to the client
                  </CustomTypography>
                </Grid2>
              </Grid2>
              {over || short ? (
                <InlineFormError message={message} sx={{ mt: 1, mb: 0 }} />
              ) : (
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  color={colors.successGreen}
                  sx={{ mt: 1 }}
                >
                  {message}
                </CustomTypography>
              )}
            </Box>
          );
        })}
        <InlineFormError message={submitError} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton label="Cancel" variant="grayscale" onClick={onClose} />
        <AppButton
          label="Delete Order"
          variant="grayscale"
          customStyles={{ color: colors.errorRed }}
          disabled={hasMismatch}
          onClick={handleConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PodDeleteSettlementDialog;
